'use client';

import { useClientSide } from '@carvana/merch-react-hooks';
import { MfeLoad } from '@carvana/microfrontend';
import { MfeReactComponent } from '@carvana/microfrontend-react';
import { usePathname } from 'next/navigation';
import { useEffect, useRef } from 'react';
import { getMfeCacheBuster } from '@/common/MicroFrontEnds';
import { assetsCdnUrl, commonEnvironment } from '~/constants/environment';
import { wait } from '~/utilities';
import { withErrorBoundary } from '~/utilities/ErrorBoundary';
import MinifiedFooter from './MinifiedFooter/MinifiedFooter';

function getFooterClassname(pathname: string | null): string | undefined {
  if (!pathname) return undefined;

  if (pathname.startsWith('/cars')) {
    return 'bg-background-default';
  }

  if (pathname.startsWith('/new')) {
    return 'bg-background-weak';
  }

  if (pathname.startsWith('/vehicle')) {
    return 'bg-canvas-muted';
  }

  return undefined;
}

function getShouldUseMinifiedFooter(pathname: string | null): boolean {
  if (!pathname) return false;

  return pathname.startsWith('/new');
}

type OwnProps = {
  className?: string;
};

/**
 * Client Component for PageFooter
 * This is a simplfied page footer that have access to context providers
 * The server component CarvanaLayoutFooter handles the context logic
 */
function PageFooter({ className }: OwnProps) {
  const isClientSide = useClientSide();
  const pathname = usePathname();
  const resolvedClassName = className ?? getFooterClassname(pathname);
  const shouldUseMinifiedFooter = getShouldUseMinifiedFooter(pathname);

  const hasLoadedMfe = useRef(false);

  useEffect(() => {
    if (shouldUseMinifiedFooter || hasLoadedMfe.current) return;

    let cancelled = false;

    wait(100).then(() => {
      if (cancelled) return;

      hasLoadedMfe.current = true;
      void MfeLoad({
        files: [
          [
            'FooterModule',
            [
              {
                component: 'default',
                browser: true,
                default: {
                  file: 'index.js',
                  manifest: `${assetsCdnUrl}/footer/module/assets-manifest.json?v=${getMfeCacheBuster()}`
                }
              }
            ]
          ]
        ]
      });
    });

    return () => {
      cancelled = true;
    };
  }, [shouldUseMinifiedFooter]);

  if (shouldUseMinifiedFooter) {
    return <MinifiedFooter className={resolvedClassName} />;
  }

  return isClientSide ? (
    <MfeReactComponent
      component="default"
      module="FooterModule"
      environment={commonEnvironment}
      withoutProvider
      wrapperClassName={resolvedClassName}
    />
  ) : null;
}

export default withErrorBoundary(PageFooter, { section: 'PageFooter' });
